import {ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, OnDestroy} from '@angular/core';
import {BaseComponent} from '../../../../components/general/base/base.component';
import {_} from '../../../utils';
import {groupBy} from 'lodash-es';
import {takeUntil, filter, scan, switchMap, withLatestFrom, map} from 'rxjs/operators';
import {BehaviorSubject, Subject, combineLatest} from 'rxjs';
import { MapService, MapStyle } from '../../map.service';
import { CanvasLayer, Layer } from '../../layer/layer.model';
import { LayerGroup } from '../../layer/layer-group.model';
import { FlagService } from '~app/shared/flag/flag.service';
import { IFlag } from '~app/shared/flag/flag.model';
import { MapElement } from '../../map-element.model';
import { getSharedLayer } from '../../layer/layer-definitions/shared';
import { Feature, FeatureCollection, Geometry, Point } from 'geojson';

@Component({
  selector: 'qm-map-settings',
  templateUrl: './map-settings.component.html',
  styleUrls: ['./map-settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MapSettingsComponent extends BaseComponent implements OnInit, OnDestroy {
  /**
   * Current map style
   */
  public currentStyle: MapStyle;

  /**
   * Array of possible map styles
   */
  public styles: MapStyle[] = [];
  /**
   * Visible filters
   */
  public layerGroupMap: Map<string, {group: LayerGroup, expanded: boolean}> = new Map();
  public loadedLayers = new Set();
  public currentZoomLevel: number;

  private flagLayerFilter = new BehaviorSubject<boolean>(false);
  public flagLayerFilter$ = this.flagLayerFilter.asObservable();
  private flags: IFlag[];
  //// NOTE: this is a heavy map (too heavy), maybe GL expression insted !
  private layerMapElements: Map<string, MapElement<Point, any>[]> = new Map();

  public notGroupedLayers$ = this.mapService.layersR$.pipe(
    filter(layer =>
      layer.metadata?.group === undefined           // Layer has no group
        && layer.metadata?.userTogglable !== false  // Layer is shown as a button in settings
    ),
    scan((acc: Layer[], layer) => [...acc, layer], [])
  );

  public hasMultipleMarkerModes = this.mapService.getHasMultipleMarkerModes();

  /**
   * Subscription manager
   */
  private ngUnsubscribe = new Subject<void>();

  constructor(private changeDetector: ChangeDetectorRef,
              private mapService: MapService,
              private flagService: FlagService) {
    super();
  }

  async ngOnInit() {
    this.styles = this.mapService.getAvailableStyles();
    this.currentStyle = this.mapService.getStyle();

    this.flagService.flags$
      .pipe(
        takeUntil(this.ngUnsubscribe),
      ).subscribe(flags => {
        this.flags = flags;
        this.changeDetector.markForCheck();
      });

    /**
     * Layers' loading status
     */
    this.mapService.loadedLayers$
      .pipe(
        takeUntil(this.ngUnsubscribe),
      ).subscribe(layer => {
        this.loadedLayers.add(layer.id);
        //// enrich the properties with flagging status
        const elements = layer.getFeatures().map(mapElem => {
          if (this.flags.some((flag) => flag.targetObjectId === mapElem.properties?.Positiotunnus)) {
            return new MapElement<Point>(mapElem.id, layer, {...mapElem.properties, flagged: true}, mapElem.type, mapElem.geometry)
          } else {
            return new MapElement<Point>(mapElem.id, layer, {...mapElem.properties, flagged: false}, mapElem.type, mapElem.geometry)
          }
        });
        layer.pushFeatures(elements);
        this.layerMapElements.set(layer.id, elements)
        console.log('enriched MapElements for ',layer.id);
        this.changeDetector.markForCheck();
      });

    this.flagLayerFilter$
      .pipe(
        takeUntil(this.ngUnsubscribe)
      ).subscribe(flagFilterState => {
        console.log(`flagFilter status changed to ${flagFilterState}`);
        const layerList = this.mapService.getLayerList();
        const flagLayer = layerList.find(layer => layer.id === 'flagLayer');
        for (const layer of layerList) {
          if (layer.id !== 'flagLayer') {
            const flaggedFeatures = layer.getFeatures().filter(mapElem => mapElem.properties?.flagged);
            if (flagFilterState) {
              layer.pushFeatures(flaggedFeatures.map(mapElem => new MapElement<Point>(mapElem.id, layer, mapElem.properties, mapElem.type, mapElem.geometry)));
            } else {
              layer.pushFeatures(this.layerMapElements.get(layer.id));
            }
          }
        }
        this.changeDetector.markForCheck();
    });

    this.mapService.zoom$
      .pipe(
        takeUntil(this.ngUnsubscribe)
      ).subscribe(zoomLevel => {
        this.currentZoomLevel = zoomLevel;
        this.changeDetector.markForCheck();
      });

    this.mapService.layerGroups$
      .pipe(
        takeUntil(this.ngUnsubscribe)
      ).subscribe(layerGroups => {
        this.layerGroupMap = this.createLayerGroupMap(layerGroups);
        this.changeDetector.markForCheck();
      });
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  toggleLayerGroupExpanded(group: any) {
    group.expanded = !group.expanded;
  }

  /**
   * Set map style
   */
  public setStyle(style: MapStyle) {
    this.mapService.setStyle(style);
  }

  public createLayerGroupMap(layerGroups: LayerGroup[]): Map<string, {group: LayerGroup, expanded: boolean}> {
    console.log('get groups');
    const layerGroupMap = new Map();
    const layers = this.mapService.getLayerList().filter((layer: Layer) => {
      return layer.metadata.group;
    });
    const groups = Object.keys(groupBy(layers, 'metadata.group'));
    for (const group of layerGroups) {
      if (group) {
        const temp: Layer[] = [];
        layers.forEach(layer => {
          if (layer.metadata.group && layer.metadata.group === group.name) {
            temp.push(layer);
          }
        });
        layerGroupMap.set(group.name, {group, expanded: false});
      }
    }
    return layerGroupMap;
  }

  toggleGroupVisibility($event: MouseEvent, layerGroup: LayerGroup) {
    layerGroup.setVisibility(($event.target as HTMLInputElement).checked);
  }

  toggleFlagVisibility($event: MouseEvent) {
    this.flagLayerFilter.next(!this.flagLayerFilter.getValue());
  }

  flagFilterChecked(): boolean {
    return this.flagLayerFilter.getValue();
  }

  allLayersLoaded(groupName: string): boolean {
    if (this.layerGroupMap.has(groupName)) {
      const layerGroup = this.layerGroupMap.get(groupName);
      const notLoadedLayer = layerGroup.group.layers.find(i => !this.loadedLayers.has(i.id));
      return notLoadedLayer === undefined; // There is no layer that is not loaded
    }
    return false;
  }

  public getVisibleLayerCount(layerGroup: string): string {
    if (this.layerGroupMap.has(layerGroup)) {
      const group = this.layerGroupMap.get(layerGroup);
      return group.group.layers.filter(layer => layer.getIsSelected()).length + '/' + group.group.layers.length;
    }
    return '-';
  }

  zoomTo(event: MouseEvent, layerVisibilityLevel: number) {
    event.stopPropagation();
    this.mapService.zoomTo(layerVisibilityLevel);
  }
  public clearLayerSelections(group: LayerGroup) {
    group.layers.forEach(layer => layer.setIsSelected(false));
  }

  compareFnSelect(s1: {name: string, url: string}, s2: {name: string, url: string}) {
    if (s1 && s2) {
      return s1.url === s2.url;
    }
  }
}
